<template>
  <div class="product-attributes">
    <h3>{{ header }}</h3>
    <table>
      <thead>
        <tr>
          <th>Attribute</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(value, key) in displayedAttributes"
          :key="key"
          :class="{ flagged: isFlagged(key), highlighted: isHighlighted(key) }"
          @mouseenter="hoveredRow = key; showTooltip($event, key)"
          @mouseleave="hoveredRow = null; hideTooltip()"
        >
          <td class="attribute-key">{{ formatKey(key) }}</td>
          <td class="attribute-value">
            <span v-if="isMatchingDefault(key)" class="matching-indicator">
              <!-- SVG Checkmark Icon - Bootstrap Success Green -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="icon icon-tabler icon-tabler-check"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="#28a745"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                style="vertical-align: middle; margin-right: 5px;"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M5 12l5 5l10 -10" />
              </svg>
            </span>
            <span
              v-if="isEditedData && isEditable"
              contenteditable
              @input="onAttributeEdit(key, $event)"
              class="editable-attribute"
            >
              {{ formatValue(value) }}
            </span>
            <span v-else>{{ formatValue(value) }}</span>
          </td>
        </tr>
      </tbody>
    </table>
    <!-- Tooltip for sources -->
    <div
      v-if="tooltipVisible"
      class="tooltip"
      :style="{ top: tooltipStyles.top, left: tooltipStyles.left }"
    >
      <p><strong>Sources:</strong></p>
      <ul>
        <li v-for="source in currentSources" :key="source">
          <span
            class="source-label"
            :style="{ backgroundColor: getSourceColor(source) }"
          >
            {{ source }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductAttributes',
  props: {
    attributesMatchingDefault: {
      type: Object,
      default: () => ({}),
    },
    selectedSource: {
      type: String,
      default: '',
    },
    originalAttributes: {
      type: Object,
      default: () => ({}),
    },
    editedAttributes: {
      type: Object,
      default: () => ({}),
    },
    attributesSources: {
      type: Object,
      default: () => ({}),
    },
    flaggedAttributes: {
      type: Object,
      default: () => ({}),
    },
    header: {
      type: String,
      default: 'Attributes',
    },
    isEditedData: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editStates: {},
      editValues: {},
      hoveredRow: null,
      tooltipVisible: false,
      tooltipStyles: {
        top: '0px',
        left: '0px',
      },
      currentSources: [],
      sourceColors: {
        Image: '#FF6F61',     // Image sources (Coral)
        Existing: '#6B5B95',  // Existing sources (Purple)
        Vendor: '#88B04B',    // Vendor sources (Green)
        External: '#92A8D1',  // External sources (Blue)
        Unknown: '#D3D3D3',   // Unknown sources (Light Gray)
      },
    };
  },
  computed: {
    hasAttributes() {
      return Object.keys(this.displayedAttributes).length > 0;
    },
    displayedAttributes() {
      // Merge original attributes with edited attributes
      let attributes = { ...this.originalAttributes, ...this.editedAttributes };
      return attributes;
    },
    displayedValues() {
      const values = {};
      if (this.displayedAttributes) {
        for (const key in this.displayedAttributes) {
          if (this.isEditedData && this.editValues[key] !== undefined) {
            values[key] = this.editValues[key];
          } else {
            values[key] = this.displayedAttributes[key];
          }
        }
      }
      return values;
    },
  },
  watch: {
    displayedAttributes() {
      // Reset edit states when attributes change
      this.editStates = {};
      // Preserve existing editValues
    },
  },
  methods: {
    formatKey(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());
    },
    formatValue(value) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value;
    },
    isHighlighted(key) {
      return this.hoveredRow === key;
    },
    isMatchingDefault(key) {
      if (!this.selectedSource) {
        return false; // Do not display indication when "No Filter" is selected
      }
      return this.attributesMatchingDefault[key] === true;
    },
    isFlagged(key) {
      return this.flaggedAttributes[key] === true;
    },
    startEditing(key) {
      // Initialize editing state and value
      this.editStates[key] = true;
      if (this.editValues[key] === undefined) {
        this.editValues[key] = this.displayedAttributes[key];
      }
    },
    stopEditing(key) {
      this.editStates[key] = false;
      const newValue = this.editValues[key];
      // Emit the edited attribute to the parent component
      this.$emit('attributeEdited', { key, value: newValue });
    },
    onAttributeEdit(key, event) {
      const newValue = event.target.innerText.trim();
      this.$emit('attributeEdited', { key, value: newValue });
    },
    showTooltip(event, key) {
      let sources = this.attributesSources[key];

      // Normalize sources to an array
      if (Array.isArray(sources)) {
        // Do nothing, sources is already an array
      } else if (sources && typeof sources === 'object') {
        // Collect all sources from nested objects into an array
        sources = Object.values(sources).reduce((acc, val) => acc.concat(val), []);
      } else if (typeof sources === 'string') {
        sources = [sources];
      } else {
        sources = [];
      }

      if (sources.length > 0) {
        this.currentSources = sources;
        this.tooltipVisible = true;

        // Calculate tooltip position to prevent overflow
        const rect = event.target.getBoundingClientRect();
        const tooltipWidth = 220; // Adjust if needed
        const tooltipHeight = 100; // Adjust if needed

        let top = rect.top + window.scrollY - tooltipHeight - 10; // Position above the cell
        let left = rect.left + window.scrollX;

        // Ensure the tooltip doesn't go off the top of the screen
        if (top < window.scrollY) {
          top = rect.bottom + window.scrollY + 10; // Position below the cell if it would go off-screen
        }

        // Adjust left if the tooltip goes off the right edge
        if (left + tooltipWidth > window.innerWidth + window.scrollX) {
          left = window.innerWidth + window.scrollX - tooltipWidth - 10;
        }

        this.tooltipStyles.top = `${top}px`;
        this.tooltipStyles.left = `${left}px`;
      }
    },
    hideTooltip() {
      this.tooltipVisible = false;
      this.currentSources = [];
    },
    getSourceColor(source) {
      const category = this.getSourceCategory(source);
      return this.sourceColors[category] || '#333';
    },
    getSourceCategory(source) {
      // Define the mapping from source names to categories
      const mapping = {
        // Image sources
        'image_allium': 'Image',
        'image_manor': 'Image',
        // Existing sources
        'existing_allium': 'Existing',
        'existing_manor': 'Existing',
        // Vendor sources
        'vendor_allium': 'Vendor',
        'vendor_manor': 'Vendor',
        // External sources
        'external_allium': 'External',
        'external_manor': 'External',

        // Image sources
        'image recognition': 'Image',
        'image_recognition': 'Image',
        'image recognition model': 'Image',
        'image tagging': 'Image',
        'visual ai': 'Image',
        'image': 'Image',
        'vision': 'Image',

        // Existing sources
        'existing data': 'Existing',
        'legacy system': 'Existing',
        'manual entry': 'Existing',
        'existing': 'Existing',
        'product title': 'Existing',
        'product description': 'Existing',
        'manual': 'Existing',

        // Vendor sources
        'vendor data': 'Vendor',
        'supplier info': 'Vendor',
        'manufacturer data': 'Vendor',
        'vendor': 'Vendor',

        // External sources
        'external data': 'External',
        'third-party api': 'External',
        'web scraping': 'External',
        'external': 'External',
        'google shopping': 'External',
        'amazon': 'External',
        'competitor data': 'External',

        // Default to 'Unknown' if not mapped
      };

      // Normalize the source string for case-insensitive matching
      const normalizedSource = source.trim().toLowerCase();

      // Attempt to find the source category
      for (const [key, category] of Object.entries(mapping)) {
        if (normalizedSource === key) {
          return category;
        }
      }

      return 'Unknown';
    },
  },
};
</script>

<style scoped>
.product-attributes {
  padding: 20px;
}

.product-attributes h2,
.product-attributes h3 {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
}

.attributes-container {
  max-height: 350px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.product-attributes table {
  width: 100%;
  border-collapse: collapse;
}

.product-attributes th,
.product-attributes td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.attribute-key {
  background-color: #f9fafb;
  font-weight: bold;
  width: 40%;
  word-break: break-word;
}

.attribute-value {
  background-color: #fff;
  cursor: pointer;
  word-break: break-word;
}

.attribute-value:hover {
  background-color: #f0f0f0;
}

.flagged .attribute-key,
.flagged .attribute-value {
  background-color: #ffe6e6;
}

.tooltip {
  position: fixed;
  z-index: 1000;
  background-color: rgba(51, 51, 51, 0.9);
  color: #fff;
  padding: 10px;
  border-radius: 5px;
  max-width: 220px;
  word-wrap: break-word;
}

.tooltip p {
  margin: 0 0 5px 0;
}

.tooltip ul {
  margin: 0;
  padding-left: 0;
  list-style-type: none;
}

.tooltip ul li {
  margin-bottom: 5px;
}

.source-label {
  display: inline-block;
  color: #fff;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 14px;
}

/* Hover effect */
.product-attributes tbody tr:hover {
  background-color: #f0f0f0;
}

.matching-indicator {
  display: inline-flex;
  align-items: center;
}

.editable-attribute {
  outline: none;
  border-bottom: 1px dashed #ccc;
  cursor: text;
}

.editable-attribute:focus {
  border-bottom: 1px dashed #2c3e50;
}
</style>
